<template>
  <SatellitesPage :title="title" :back="{ name: 'SatelliteGroups' }" :back-label="$t('satellites.tabs.groups')">
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <div v-else class="group-layout">
      <!-- Name -->
      <section class="sat-card">
        <div class="sat-field group-name">
          <label class="sat-field__label" for="group-name">{{ $t("satellites.groups.name") }}</label>
          <input id="group-name" ref="name" v-model="form.name" type="text" maxlength="64" :placeholder="$t('satellites.groups.name_placeholder')" :class="{ invalid: !!violation('name') }" />
          <div v-if="violation('name')" class="sat-field__error">{{ violation("name") }}</div>
        </div>
      </section>

      <!-- Members -->
      <section class="sat-card">
        <h2 class="sat-card__title">
          {{ $t("satellites.groups.members_title") }}
          <span class="sat-chip">{{ form.satelliteIds.length }}</span>
        </h2>
        <p class="sat-card__subtitle">{{ $t("satellites.groups.members_hint") }}</p>

        <div v-if="!members.length" class="group-members__empty">{{ $t("satellites.groups.no_members") }}</div>
        <div v-for="satellite in members" :key="satellite.id" class="group-member" :class="{ _new: !initialIds.includes(satellite.id) }">
          <span class="sat-dot" :class="statusDot(satellite.status)"></span>
          <router-link :to="{ name: 'Satellite', params: { id: satellite.id } }" class="group-member__name">{{ satName(satellite) }}</router-link>
          <span class="group-member__ip sat-mono"><CopyIp v-if="satellite.ip" :ip="satellite.ip" /><template v-else>—</template></span>
          <span v-if="!initialIds.includes(satellite.id)" class="sat-chip _accent">{{ $t("satellites.groups.joins") }}</span>
          <button type="button" class="sat-icon-btn" :title="$t('satellites.groups.remove_member')" @click="removeMember(satellite.id)"><SatIcon name="x" :size="15" /></button>
        </div>
        <div v-for="satellite in leaving" :key="`leaving-${satellite.id}`" class="group-member _leaving">
          <span class="sat-dot"></span>
          <span class="group-member__name">{{ satName(satellite) }}</span>
          <span class="sat-chip _warn">{{ $t("satellites.groups.leaves") }}</span>
          <button type="button" class="sat-icon-btn" :title="$t('satellites.groups.undo')" @click="addMember(satellite.id)"><SatIcon name="refresh" :size="14" /></button>
        </div>

        <SatPopover :width="360" class="mt-3">
          <template #trigger>
            <button type="button" class="btn _border _green sat-btn-inline" :disabled="!candidates.length"><SatIcon name="plus" /> {{ $t("satellites.groups.add_members") }}</button>
          </template>
          <template #default>
            <div class="sat-menu">
              <button v-for="satellite in candidates" :key="satellite.id" type="button" class="sat-menu__item" @click="addMember(satellite.id)">
                <span class="sat-dot" :class="statusDot(satellite.status)"></span>
                <span>{{ satName(satellite) }}</span>
                <span class="sat-menu__meta">{{ satellite.group ? satellite.group.name : $t("satellites.groups.none") }}</span>
              </button>
            </div>
          </template>
        </SatPopover>

        <div v-if="joiningNote" class="sat-note _warn mt-3"><SatIcon name="alert" /><span>{{ joiningNote }}</span></div>
        <div v-if="leaving.length" class="sat-note mt-3"><SatIcon name="info" /><span>{{ $t("satellites.groups.leaving_note") }}</span></div>
      </section>

      <!-- Settings -->
      <section class="sat-card">
        <h2 class="sat-card__title">{{ $t("satellites.page.settings") }}</h2>
        <p class="sat-card__subtitle">{{ $t("satellites.groups.settings_hint") }}</p>
        <SettingsForm v-model="form.settings" :definitions="definitions" :violations="violations" :disabled="saving" />
      </section>

      <!-- Domains -->
      <section v-if="!isNew" class="sat-card">
        <h2 class="sat-card__title">
          {{ $t("satellites.page.domains") }}
          <span class="sat-chip">{{ domains.length }}</span>
        </h2>
        <p class="sat-card__subtitle">{{ $t("satellites.groups.domains_hint") }}</p>
        <DomainList :domains="domains" :empty-text="$t('satellites.groups.no_domains')" />
        <router-link :to="{ name: 'SatelliteDomains', query: { group: groupId } }" class="sat-link group-manage">{{ $t("satellites.page.manage_domains") }} <SatIcon name="chevron-right" :size="14" /></router-link>
      </section>

      <section v-if="!isNew" class="group-danger">
        <div>
          <div class="group-danger__title">{{ $t("satellites.groups.delete_title") }}</div>
          <div class="group-danger__text">{{ $t("satellites.groups.delete_hint") }}</div>
        </div>
        <button type="button" class="btn _border group-danger__btn" @click="openDelete">{{ $t("satellites.groups.delete") }}</button>
      </section>

      <SaveBar
        :dirty="isNew || dirty"
        :saving="saving"
        :invalid="invalid"
        :message="isNew ? $t('satellites.groups.new_message') : ''"
        :save-label="isNew ? $t('satellites.groups.create') : ''"
        @save="save"
        @discard="discard"
      />
    </div>

    <!-- Delete -->
    <modal name="satelliteGroupDelete" :width="560" height="auto" :scrollable="true" classes="creative-modal">
      <div class="group-modal">
        <svg v-svg @click="$modal.hide('satelliteGroupDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-2">{{ $t("satellites.groups.delete_confirm", { name: initialName }) }}</h3>
        <template v-if="initialIds.length">
          <p class="group-modal__text">{{ pl("satellites.groups.delete_members", initialIds.length) }}</p>
          <label class="group-choice" :class="{ _active: !deleteMoveTo }">
            <input v-model="deleteMoveTo" type="radio" :value="null" />
            <span>
              <b>{{ $t("satellites.groups.delete_keep") }}</b>
              <span>{{ $t("satellites.groups.delete_keep_hint") }}</span>
            </span>
          </label>
          <label v-if="otherGroups.length" class="group-choice" :class="{ _active: !!deleteMoveTo }">
            <input type="radio" :checked="!!deleteMoveTo" @change="deleteMoveTo = otherGroups[0].id" />
            <span>
              <b>{{ $t("satellites.groups.delete_move") }}</b>
              <span>{{ $t("satellites.groups.delete_move_hint") }}</span>
              <GroupSelect v-if="deleteMoveTo" v-model="deleteMoveTo" :groups="otherGroups" class="mt-2" />
            </span>
          </label>
        </template>
        <p v-else class="group-modal__text">{{ $t("satellites.groups.delete_empty") }}</p>
        <div class="d-flex justify-content-end mt-4" style="gap: 10px">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteGroupDelete')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green" :disabled="deleting" @click="remove">{{ $t("satellites.groups.delete") }}</button>
        </div>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import { isEqual } from "lodash";
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import CopyIp from "@/components/Satellites/CopyIp.vue";
import SatPopover from "@/components/Satellites/SatPopover.vue";
import SettingsForm from "@/components/Satellites/SettingsForm.vue";
import SaveBar from "@/components/Satellites/SaveBar.vue";
import DomainList from "@/components/Satellites/DomainList.vue";
import GroupSelect from "@/components/Satellites/GroupSelect.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk, messagesOf } from "@/components/Satellites/format";
import { confirmLeave } from "@/components/Satellites/unsaved";
import { validateSetting } from "@/components/Satellites/settingsSchema";

export default {
  name: "SatelliteGroup",
  components: { CopyIp, SatellitesPage, SatIcon, SatPopover, SettingsForm, SaveBar, DomainList, GroupSelect },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  beforeRouteLeave(to, from, next) {
    confirmLeave(this, next);
  },
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      definitions: [],
      satellites: [],
      groups: [],
      // Saved state
      initialName: "",
      initialIds: [],
      settings: {},
      domains: [],
      form: { name: "", satelliteIds: [], settings: {} },
      violations: {},
      deleteMoveTo: null,
    };
  },
  computed: {
    isNew() {
      return this.$route.params.id === "new";
    },
    groupId() {
      return this.isNew ? null : this.$route.params.id;
    },
    title() {
      if (this.isNew) return this.$t("satellites.groups.new_title");
      return this.initialName;
    },
    dirty() {
      return (
        this.form.name.trim() !== this.initialName ||
        !isEqual([...this.form.satelliteIds].sort(), [...this.initialIds].sort()) ||
        !isEqual(this.form.settings, this.settings)
      );
    },
    invalid() {
      return !this.form.name.trim() || this.definitions.some((d) => validateSetting(d, d.key in this.form.settings ? this.form.settings[d.key] : d.default));
    },
    members() {
      return this.form.satelliteIds.map((id) => this.satellites.find((s) => s.id === id)).filter(Boolean);
    },
    leaving() {
      return this.initialIds.filter((id) => !this.form.satelliteIds.includes(id)).map((id) => this.satellites.find((s) => s.id === id)).filter(Boolean);
    },
    candidates() {
      return this.satellites.filter((s) => !this.form.satelliteIds.includes(s.id));
    },
    joiningNote() {
      const joining = this.members.filter((s) => !this.initialIds.includes(s.id));
      if (!joining.length) return "";
      const fromGroups = joining.filter((s) => s.group).length;
      return this.pl("satellites.groups.joining_note", joining.length, { moved: fromGroups });
    },
    otherGroups() {
      return this.groups.filter((g) => g.id !== this.groupId);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      const requests = [satelliteService.getSettingsSchema(), satelliteService.getSatellites(), satelliteService.getGroups()];
      if (!this.isNew) requests.push(satelliteService.getGroup(this.groupId));
      return Promise.all(requests)
        .then(([schema, satellites, groups, detail]) => {
          if (isOk(schema)) this.definitions = schema.data.definitions || [];
          if (isOk(satellites)) this.satellites = satellites.data || [];
          if (isOk(groups)) this.groups = groups.data || [];
          if (this.isNew) {
            this.settings = Object.fromEntries(this.definitions.map((d) => [d.key, d.default]));
            const preselected = String(this.$route.query.satellites || "").split(",").map(Number).filter((id) => this.satellites.some((s) => s.id === id));
            this.reset();
            this.form.satelliteIds = preselected;
            this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
          } else if (isOk(detail)) {
            this.apply(detail.data);
          } else {
            if (detail && detail.status === 404) this.$router.replace({ name: "SatelliteGroups" }).catch(() => {});
            else this.showError(detail);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    apply(data) {
      this.initialName = data.group.name;
      this.initialIds = data.satellites.map((s) => s.id);
      this.settings = { ...data.settings };
      this.domains = data.domains || [];
      // The members as the backend sees them now
      const byId = Object.fromEntries(data.satellites.map((s) => [s.id, s]));
      this.satellites = this.satellites.map((s) => byId[s.id] || s);
      this.reset();
    },
    reset() {
      this.form = { name: this.initialName, satelliteIds: [...this.initialIds], settings: { ...this.settings } };
      this.violations = {};
    },
    discard() {
      if (this.isNew) this.$router.push({ name: "SatelliteGroups" }).catch(() => {});
      else this.reset();
    },
    statusDot(status) {
      return { online: "_ok", offline: "_danger" }[status] || "";
    },
    addMember(id) {
      if (!this.form.satelliteIds.includes(id)) this.form.satelliteIds = [...this.form.satelliteIds, id];
    },
    removeMember(id) {
      this.form.satelliteIds = this.form.satelliteIds.filter((item) => item !== id);
    },
    violation(key) {
      return messagesOf(this.violations[key]);
    },
    save() {
      if (this.saving || this.invalid) return;
      this.saving = true;
      this.violations = {};
      const request = this.isNew
        ? satelliteService.createGroup({ name: this.form.name.trim(), settings: this.form.settings, satellite_ids: this.form.satelliteIds })
        : satelliteService.updateGroup(this.groupId, this.changes());
      request
        .then((res) => {
          if (isOk(res)) {
            this.apply(res.data);
            this.$alert({ type: "success", title: this.$t(this.isNew ? "satellites.groups.created" : "satellites.groups.saved") });
            if (this.isNew) this.$router.replace({ name: "SatelliteGroup", params: { id: res.data.group.id } }).catch(() => {});
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    changes() {
      const body = {};
      if (this.form.name.trim() !== this.initialName) body.name = this.form.name.trim();
      if (!isEqual([...this.form.satelliteIds].sort(), [...this.initialIds].sort())) body.satellite_ids = this.form.satelliteIds;
      if (!isEqual(this.form.settings, this.settings)) body.settings = this.form.settings;
      return body;
    },
    openDelete() {
      this.deleteMoveTo = null;
      this.$modal.show("satelliteGroupDelete");
    },
    remove() {
      if (this.deleting) return;
      this.deleting = true;
      satelliteService
        .deleteGroup(this.groupId, this.deleteMoveTo)
        .then((res) => {
          if (isOk(res)) {
            this.$modal.hide("satelliteGroupDelete");
            this.$alert({ type: "success", title: this.$t("satellites.groups.deleted") });
            this.form = { name: this.initialName, satelliteIds: [...this.initialIds], settings: { ...this.settings } };
            this.$router.push({ name: "SatelliteGroups" }).catch(() => {});
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.group-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 920px;
}

.group-name input {
  width: 100%;
  max-width: 420px;
}

.group-members__empty {
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 13px;
  color: var(--sat-muted);
  background: var(--sat-surface);
}

.group-member {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--sat-border);

  &._leaving .group-member__name {
    color: var(--sat-muted);
    text-decoration: line-through;
  }
}

.group-member__name {
  min-width: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--sat-text);

  &:hover {
    color: var(--sat-accent);
  }
}

.group-member__ip {
  margin-left: auto;
  color: var(--sat-muted);
}

.group-manage {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 14px;
  font-size: 14px;
}

.group-danger {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border: 1px dashed var(--sat-border);
  border-radius: 16px;
}

.group-danger__title {
  font-size: 14px;
  font-weight: 700;
}

.group-danger__text {
  margin-top: 2px;
  font-size: 13px;
  color: var(--sat-muted);
}

.group-danger__btn {
  color: var(--sat-danger) !important;
  border-color: var(--sat-danger) !important;
  background: transparent !important;

  &:hover {
    color: #fff !important;
    background: var(--sat-danger) !important;
  }
}

.group-modal {
  position: relative;
  padding: 16px 24px 24px;
}

.group-modal__text {
  margin: 12px 0 16px;
  font-size: 14px;
  text-align: center;
  color: var(--sat-text-2);
}

.group-choice {
  display: flex;
  gap: 12px;
  margin: 0 0 10px;
  padding: 14px;
  border: 1px solid var(--sat-border);
  border-radius: 12px;
  cursor: pointer;

  &._active {
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);
  }

  input {
    margin-top: 3px;
    accent-color: var(--sat-accent);
  }

  > span {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
    color: var(--sat-muted);

    b {
      font-size: 14px;
      color: var(--sat-text);
    }
  }
}
</style>
